import { Injectable } from '@angular/core';
import { OpportunityEffort, OpportunityItem, OpportunityType } from '../../opportunities/models/opportunity.models';
import {
  SeoPdfBlock,
  SeoPdfDataAvailability,
  SeoPdfExecutiveCallout,
  SeoPdfExecutiveSummary,
  SeoPdfHealthSummary,
  SeoPdfMetricCard,
  SeoPdfOpportunityCluster,
  SeoPdfPage,
  SeoPdfReportViewModel,
  SeoPdfSourceData,
  SeoPdfStatusTone,
  SeoPdfSummaryTableColumn,
  SeoPdfSummaryTableRow,
  SeoPdfTechnicalIssue,
} from './seo-comprehensive-pdf.models';

@Injectable({
  providedIn: 'root',
})
export class SeoComprehensivePdfAdapterService {
  public buildReport(source: SeoPdfSourceData): SeoPdfReportViewModel {
    const opportunityClusters = this.clusterOpportunities(source.opportunities?.items ?? []);
    const dataAvailability = this.buildDataAvailability(source);
    const healthSummary = this.buildHealthSummary(source, opportunityClusters);
    const executiveSummary = this.buildExecutiveSummary(source, opportunityClusters, healthSummary, dataAvailability);
    const pages = this.buildPages(source, opportunityClusters, dataAvailability);

    return {
      project: source.project,
      dataAvailability,
      executiveSummary,
      healthSummary,
      pages: pages.filter((page: SeoPdfPage) => page.visible),
      opportunityClusters,
    };
  }

  public clusterOpportunities(items: OpportunityItem[]): SeoPdfOpportunityCluster[] {
    const clusters = new Map<string, SeoPdfOpportunityCluster>();

    items.forEach((item: OpportunityItem) => {
      const key = this.buildOpportunityClusterKey(item);
      const existingCluster = clusters.get(key);

      if (existingCluster) {
        existingCluster.count += 1;
        existingCluster.types = this.mergeOpportunityTypes(existingCluster.types, item.type);
        existingCluster.totalClicks += item.estimatedImpact?.clicks ?? 0;
        existingCluster.totalImpressions += item.estimatedImpact?.impressions ?? 0;
        existingCluster.items.push(item);
        existingCluster.representativeEvidence = this.mergeUniqueStrings(
          existingCluster.representativeEvidence,
          this.extractEvidenceSummaries(item)
        );
        existingCluster.actionSteps = this.mergeUniqueStrings(existingCluster.actionSteps, item.actionSteps ?? []);
        existingCluster.effort = this.mergeEffort(existingCluster.effort, item.effort);
        return;
      }

      clusters.set(key, {
        key,
        label: this.buildOpportunityClusterLabel(item),
        family: this.getOpportunityFamily(item.type),
        count: 1,
        types: [item.type],
        effort: item.effort ?? 'UNKNOWN',
        totalClicks: item.estimatedImpact?.clicks ?? 0,
        totalImpressions: item.estimatedImpact?.impressions ?? 0,
        items: [item],
        representativeEvidence: this.extractEvidenceSummaries(item),
        actionSteps: [...(item.actionSteps ?? [])],
      });
    });

    return [...clusters.values()].sort((left: SeoPdfOpportunityCluster, right: SeoPdfOpportunityCluster) => {
      const impactDifference = right.totalClicks - left.totalClicks;
      if (impactDifference !== 0) {
        return impactDifference;
      }

      return right.count - left.count;
    });
  }

  private buildExecutiveSummary(
    source: SeoPdfSourceData,
    opportunityClusters: SeoPdfOpportunityCluster[],
    healthSummary: SeoPdfHealthSummary,
    dataAvailability: SeoPdfDataAvailability
  ): SeoPdfExecutiveSummary {
    const metrics: SeoPdfMetricCard[] = [
      this.buildMetricCard('Domain Rank', source.dashboard?.domainRank),
      this.buildMetricCard('Organic Traffic', source.dashboard?.organicTraffic),
      this.buildMetricCard('Backlinks', source.dashboard?.backlinks),
      this.buildMetricCard('Open Opportunities', source.opportunities?.items?.length ?? 0),
      this.buildMetricCard('Site Health Score', source.technicalHealth?.siteHealthScore),
    ];

    const callouts: SeoPdfExecutiveCallout[] = [
      {
        label: 'Strongest signal',
        message: this.getStrongestSignalMessage(source, healthSummary),
        tone: 'positive',
      },
      {
        label: 'Biggest constraint',
        message: healthSummary.risks[0] ?? 'No major constraint stood out from the available inputs.',
        tone: source.technicalHealth?.issues?.some((issue: SeoPdfTechnicalIssue) => issue.count > 0) ? 'warning' : 'neutral',
      },
      {
        label: 'Fastest win',
        message: healthSummary.opportunities[0] ?? 'No clear quick win was detected from the current data.',
        tone: opportunityClusters.length > 0 ? 'info' : 'neutral',
      },
    ];

    return {
      metrics: metrics.filter((metric: SeoPdfMetricCard) => metric.value !== '-'),
      summaryParagraph: this.buildExecutiveParagraph(source, healthSummary, dataAvailability),
      callouts,
      actions: healthSummary.opportunities.slice(0, 3),
    };
  }

  private buildHealthSummary(
    source: SeoPdfSourceData,
    opportunityClusters: SeoPdfOpportunityCluster[]
  ): SeoPdfHealthSummary {
    const issueCount = source.technicalHealth?.issues.reduce(
      (sum: number, issue: SeoPdfTechnicalIssue) => sum + Math.max(0, issue.count),
      0
    ) ?? 0;
    const overallStatus = issueCount === 0
      ? 'Healthy foundation'
      : issueCount <= 10
        ? 'Mixed performance'
        : 'At risk';

    const momentum = this.getMomentumLabel(source);
    const risks = this.buildRiskSummary(source);
    const opportunities = this.buildOpportunitySummary(opportunityClusters);

    return {
      overallStatus,
      momentum,
      risks,
      opportunities,
    };
  }

  private buildPages(
    source: SeoPdfSourceData,
    opportunityClusters: SeoPdfOpportunityCluster[],
    dataAvailability: SeoPdfDataAvailability
  ): SeoPdfPage[] {
    return [
      this.buildPerformanceSnapshotPage(source, dataAvailability),
      this.buildSearchVisibilityPage(source),
      this.buildCompetitiveLandscapePage(source),
      this.buildTechnicalHealthPage(source),
      this.buildOpportunityRoadmapPage(source, opportunityClusters),
    ];
  }

  private buildPerformanceSnapshotPage(source: SeoPdfSourceData, dataAvailability: SeoPdfDataAvailability): SeoPdfPage {
    const metrics: SeoPdfMetricCard[] = [
      this.buildMetricCard('Domain Rank', source.dashboard?.domainRank),
      this.buildMetricCard('Organic Traffic', source.dashboard?.organicTraffic),
      this.buildMetricCard('Backlinks', source.dashboard?.backlinks),
      this.buildMetricCard('Visits', source.googleAnalytics?.visits),
      this.buildMetricCard('Page Views', source.googleAnalytics?.pageViews),
      this.buildMetricCard('Bounce Rate', source.googleAnalytics?.bounceRate, true),
      this.buildMetricCard('Sessions', source.googleAnalytics?.totalSessions),
      this.buildMetricCard('Avg. Session Duration', source.googleAnalytics?.avgSessionDuration, false, 'seconds'),
    ].filter((metric: SeoPdfMetricCard) => metric.value !== '-');

    return {
      id: 'performance-snapshot',
      title: 'Performance Snapshot',
      insight: dataAvailability.gaAvailable
        ? 'Visibility and audience behavior can be evaluated together from the current export inputs.'
        : 'Search-performance signals are available, but Google Analytics behavior inputs are missing.',
      whatDataShows: dataAvailability.gaLowSignal
        ? 'Behavior inputs are present but low-signal, so the report should emphasize directional trends rather than conversion claims.'
        : this.buildPerformanceDataNarrative(source),
      whatItMeans: dataAvailability.gaAvailable
        ? 'We can connect SEO visibility with visit quality in the narrative pages.'
        : 'The MVP should degrade gracefully and avoid behavior conclusions when GA is unavailable.',
      recommendedResponse: [
        'Use KPI cards only for metrics that support the page argument.',
        'Omit referral, geography, and empty conversion widgets from the core narrative.',
      ],
      blocks: [
        {
          type: 'kpi-grid',
          payload: metrics,
        },
        {
          type: 'evidence-callout',
          payload: {
            title: 'Behavior highlights',
            notes: this.buildPerformanceHighlights(source),
          },
        },
        {
          type: 'paragraph',
          payload: this.buildGaEvidenceText(source),
        },
      ],
      visible: metrics.length > 0,
    };
  }

  private buildSearchVisibilityPage(source: SeoPdfSourceData): SeoPdfPage {
    const rows: SeoPdfSummaryTableRow[] = (source.keywordExamples ?? []).slice(0, 6).map((example) => ({
      keyword: example.keyword,
      rank: example.rank ?? '-',
      intent: example.intent ?? '-',
      volume: example.volume ?? '-',
      difficulty: example.difficulty ?? '-',
      reason: example.whyItMatters,
    }));

    return {
      id: 'search-visibility',
      title: 'Search Visibility Story',
      insight: rows.length > 0
        ? 'Representative keyword examples are available to support a curated visibility story.'
        : 'Keyword evidence is sparse, so visibility should be summarized without a detailed example table.',
      whatDataShows: rows.length > 0
        ? 'The current data set contains page-one-adjacent and topical examples that can be lifted into the report body.'
        : 'No representative keyword rows were passed into the adapter.',
      whatItMeans: rows.length > 0
        ? 'The export can explain discoverability using selective evidence rather than full raw table dumps.'
        : 'This section should be omitted until keyword evidence is wired into the export source.',
      recommendedResponse: [
        'Keep keyword evidence to 4 to 6 rows.',
        'Use the examples to explain why rankings matter, not just where they sit.',
      ],
      blocks: [
        {
          type: 'summary-table',
          payload: {
            title: 'Representative keyword examples',
            columns: [
              { key: 'keyword', label: 'Keyword' },
              { key: 'rank', label: 'Rank' },
              { key: 'intent', label: 'Intent' },
              { key: 'volume', label: 'Volume' },
              { key: 'difficulty', label: 'Difficulty' },
              { key: 'reason', label: 'Why it matters' },
            ],
            rows,
          },
        },
      ],
      visible: rows.length > 0,
    };
  }

  private buildCompetitiveLandscapePage(source: SeoPdfSourceData): SeoPdfPage {
    const rows: SeoPdfSummaryTableRow[] = (source.competitorExamples ?? []).slice(0, 3).map((example) => ({
      topic: example.topic,
      competitor: example.competitor,
      targetState: example.targetState,
      why: example.whyCompetitorWins,
      response: example.suggestedResponse,
    }));

    return {
      id: 'competitive-landscape',
      title: 'Competitive Landscape',
      insight: rows.length > 0
        ? 'Selective competitor examples are available, so the report can show where rivals are winning without dumping raw intersect tables.'
        : 'Competitor evidence is too thin to justify a standalone narrative page.',
      whatDataShows: rows.length > 0
        ? 'Domain Research contains enough ranked-keyword competitor evidence to annotate a few recoverable gaps.'
        : 'No curated competitor examples were available from the current project payload.',
      whatItMeans: rows.length > 0
        ? 'The export can frame competitor pressure as specific search topics to recover, not as a broad abstract threat.'
        : 'This page should stay conditional until stronger competitor evidence is available.',
      recommendedResponse: [
        'Use one to three annotated competitor examples only.',
        'Focus on recoverable gaps where the target is already close to page one.',
      ],
      blocks: [
        {
          type: 'evidence-callout',
          payload: {
            title: 'Competitive takeaway',
            notes: rows.length > 0
              ? [
                  `${rows[0].competitor} currently owns one of the clearest recoverable gaps.`,
                  'Use these examples as proof points, not as a full intersect export.',
                ]
              : [],
          },
        },
        {
          type: 'summary-table',
          payload: {
            title: 'Annotated competitor examples',
            columns: [
              { key: 'topic', label: 'Topic' },
              { key: 'competitor', label: 'Competitor' },
              { key: 'targetState', label: 'Target state' },
              { key: 'why', label: 'Why competitor wins' },
              { key: 'response', label: 'Suggested response' },
            ],
            rows,
          },
        },
      ],
      visible: rows.length > 0,
    };
  }

  private buildTechnicalHealthPage(source: SeoPdfSourceData): SeoPdfPage {
    const technicalHealth = source.technicalHealth;
    const prioritizedIssues = this.prioritizeTechnicalIssues(technicalHealth?.issues ?? []);
    const rows: SeoPdfSummaryTableRow[] = prioritizedIssues.slice(0, 6).map((issue: SeoPdfTechnicalIssue) => ({
      theme: issue.theme,
      status: issue.status,
      count: issue.count,
      implication: issue.businessImplication,
    }));
    const hasActiveIssues = prioritizedIssues.some((issue: SeoPdfTechnicalIssue) => issue.count > 0);

    return {
      id: 'technical-health',
      title: 'Technical Health',
      insight: rows.length === 0
        ? 'Technical inputs are not yet available for this export run.'
        : !hasActiveIssues
          ? 'The current audit looks clean enough to frame technical stability as a strength.'
          : 'Technical findings can be grouped into business-level issue themes instead of printed as a raw scan dump.',
      whatDataShows: technicalHealth
        ? `Latest scan score: ${this.formatMetricValue(technicalHealth.siteHealthScore)} with ${this.formatMetricValue(technicalHealth.crawledPages)} crawled pages.`
        : 'No site-audit summary has been connected to the adapter yet.',
      whatItMeans: rows.length > 0
        ? 'The export can summarize ranking risk by theme and still keep detailed URL inventories out of the main narrative.'
        : 'This page should stay conditional until site-audit summary data is passed in.',
      recommendedResponse: [
        'Group technical issues by crawlability, duplication, broken resources, and indexation risk.',
        'If the scan is healthy, say that clearly instead of forcing a negative narrative.',
      ],
      blocks: [
        {
          type: 'status-summary',
          payload: {
            title: 'Technical overview',
            rows,
            notes: technicalHealth?.lastCrawlAt ? [`Last crawl: ${technicalHealth.lastCrawlAt}`] : [],
          },
        },
        {
          type: 'evidence-callout',
          payload: {
            title: 'Technical takeaway',
            notes: this.buildTechnicalHighlights(source),
          },
        },
      ],
      visible: !!technicalHealth,
    };
  }

  private buildOpportunityRoadmapPage(
    source: SeoPdfSourceData,
    opportunityClusters: SeoPdfOpportunityCluster[]
  ): SeoPdfPage {
    const recommendationPayload = opportunityClusters.slice(0, 6).map((cluster: SeoPdfOpportunityCluster) => ({
      title: this.buildRoadmapTitle(cluster.label),
      family: cluster.family,
      effort: cluster.effort,
      expectedImpact: `${cluster.totalClicks.toLocaleString()} est. clicks`,
      evidenceNote: this.buildRoadmapEvidence(cluster),
      actions: cluster.actionSteps.slice(0, 3),
      count: cluster.count,
    }));

    return {
      id: 'opportunity-roadmap',
      title: 'Opportunity Roadmap',
      insight: recommendationPayload.length > 0
        ? 'The opportunity set can already be clustered into an action-oriented roadmap.'
        : 'No opportunity items were available, so the roadmap should be omitted.',
      whatDataShows: recommendationPayload.length > 0
        ? 'Repeated issue families can be grouped before rendering, which avoids exporting dense duplicate rows.'
        : 'The current export inputs do not include opportunity recommendations.',
      whatItMeans: recommendationPayload.length > 0
        ? 'The client-facing PDF can show decisions and sequencing instead of a raw operations table.'
        : 'This page will become available once the opportunities workspace is wired into the export flow.',
      recommendedResponse: [
        'Render actions as Do now and Do next groups.',
        'Keep duplicate families collapsed into one summarized action block.',
      ],
      blocks: [
        {
          type: 'recommendation-list',
          payload: recommendationPayload,
        },
      ],
      visible: recommendationPayload.length > 0 || !!source.opportunities,
    };
  }

  private buildDataAvailability(source: SeoPdfSourceData): SeoPdfDataAvailability {
    const gaAvailable = !!source.googleAnalytics;
    const gaLowSignal = gaAvailable && (source.googleAnalytics?.totalSessions ?? 0) < 100;
    const siteAuditAvailable = !!source.technicalHealth;
    const domainResearchAvailable = (source.keywordExamples?.length ?? 0) > 0 || (source.competitorExamples?.length ?? 0) > 0;
    const opportunitiesAvailable = !!source.opportunities;
    const notes: string[] = [];

    if (!gaAvailable) {
      notes.push('Google Analytics data is unavailable for this export.');
    } else if (gaLowSignal) {
      notes.push('Google Analytics volume is low, so behavior trends should be treated as directional.');
    }

    if (!siteAuditAvailable) {
      notes.push('Site audit data is unavailable for this export.');
    }

    if (!domainResearchAvailable) {
      notes.push('Domain research evidence is not yet attached to this export.');
    }

    if (!opportunitiesAvailable) {
      notes.push('Opportunities data is unavailable for this export.');
    }

    return {
      gaAvailable,
      gaLowSignal,
      siteAuditAvailable,
      domainResearchAvailable,
      opportunitiesAvailable,
      notes,
    };
  }

  private buildRiskSummary(source: SeoPdfSourceData): string[] {
    const technicalRisks = (source.technicalHealth?.issues ?? [])
      .filter((issue: SeoPdfTechnicalIssue) => issue.count > 0)
      .sort((left: SeoPdfTechnicalIssue, right: SeoPdfTechnicalIssue) => right.count - left.count)
      .slice(0, 3)
      .map((issue: SeoPdfTechnicalIssue) => `${issue.theme} affects ${issue.count} tracked issue(s).`);

    if (technicalRisks.length > 0) {
      return technicalRisks;
    }

    if (!source.googleAnalytics) {
      return ['User-behavior data is unavailable, so visit quality cannot be validated in this export.'];
    }

    return ['No significant technical or data-availability risks were identified from the current inputs.'];
  }

  private buildOpportunitySummary(clusters: SeoPdfOpportunityCluster[]): string[] {
    return clusters.slice(0, 3).map((cluster: SeoPdfOpportunityCluster) => {
      const impactLabel = cluster.totalClicks > 0
        ? `${cluster.totalClicks.toLocaleString()} estimated clicks`
        : `${cluster.count} related opportunity item(s)`;

      return `${cluster.label} should be prioritized first because it groups ${impactLabel}.`;
    });
  }

  private getStrongestSignalMessage(source: SeoPdfSourceData, healthSummary: SeoPdfHealthSummary): string {
    if ((source.dashboard?.backlinks ?? 0) > 0 && (source.opportunities?.items?.length ?? 0) > 0) {
      return 'Authority and opportunity signals are both populated, which gives the report a strong business-story backbone.';
    }

    if (source.technicalHealth && healthSummary.overallStatus === 'Healthy foundation') {
      return 'Technical stability is currently a validating strength rather than the primary constraint.';
    }

    return 'The current export inputs are strong enough to support a concise narrative-first report structure.';
  }

  private buildExecutiveParagraph(
    source: SeoPdfSourceData,
    healthSummary: SeoPdfHealthSummary,
    dataAvailability: SeoPdfDataAvailability
  ): string {
    const fragments: string[] = [];

    fragments.push(`Overall SEO status is ${healthSummary.overallStatus.toLowerCase()}.`);

    if (source.dashboard?.backlinks !== undefined || source.dashboard?.organicTraffic !== undefined) {
      fragments.push(
        `Authority and traffic inputs show ${this.describeAuthorityAndTraffic(source)}.`
      );
    }

    if (source.technicalHealth) {
      fragments.push(
        `Technical posture is ${this.describeTechnicalPosture(source.technicalHealth.issues)}.`
      );
    }

    if (dataAvailability.gaAvailable) {
      fragments.push(
        dataAvailability.gaLowSignal
          ? 'Behavior data exists, but low session volume means the report should stay directional.'
          : 'Behavior data is available to connect search visibility with visit quality.'
      );
    }

    return fragments.join(' ');
  }

  private describeAuthorityAndTraffic(source: SeoPdfSourceData): string {
    const backlinks = source.dashboard?.backlinks ?? 0;
    const organicTraffic = source.dashboard?.organicTraffic ?? 0;

    if (backlinks > 0 && organicTraffic > 0) {
      return 'both authority and estimated search demand with enough density for executive KPI coverage';
    }

    if (backlinks > 0) {
      return 'authority evidence, even though traffic inputs are still partial';
    }

    return 'partial current-state metrics that still need fuller dashboard wiring';
  }

  private buildPerformanceDataNarrative(source: SeoPdfSourceData): string {
    const hasVisitTrend = (source.googleAnalytics?.visitTrend?.length ?? 0) > 1;
    const hasBounceTrend = (source.googleAnalytics?.bounceRateTrend?.length ?? 0) > 1;

    if (hasVisitTrend && hasBounceTrend) {
      return 'Current KPI coverage includes both traffic and bounce-rate trend inputs, which is enough to support a concise current-state behavior summary.';
    }

    if (hasVisitTrend) {
      return 'Current KPI coverage includes visit-trend evidence, so the performance page can speak to direction rather than just totals.';
    }

    return 'Current KPI coverage is strong enough to support a concise current-state page.';
  }

  private buildGaEvidenceText(source: SeoPdfSourceData): string {
    const topSource = source.googleAnalytics?.trafficSources?.[0];
    const visitorMix = source.googleAnalytics?.visitorMix ?? [];
    const returningUsers = visitorMix.find((item) => item.type.toLowerCase().includes('return'));
    const newUsers = visitorMix.find((item) => item.type.toLowerCase().includes('new'));
    const evidenceParts: string[] = [];

    if (topSource) {
      evidenceParts.push(`Top traffic source in the current GA snapshot is ${topSource.source} with ${topSource.sessions.toLocaleString()} sessions.`);
    }

    if (newUsers && returningUsers) {
      evidenceParts.push(`Visitor mix shows ${newUsers.users.toLocaleString()} new users versus ${returningUsers.users.toLocaleString()} returning users.`);
    }

    if (evidenceParts.length === 0) {
      return 'GA evidence is currently limited to headline KPI totals.';
    }

    return evidenceParts.join(' ');
  }

  private buildPerformanceHighlights(source: SeoPdfSourceData): string[] {
    const highlights: string[] = [];
    const visitTrend = source.googleAnalytics?.visitTrend ?? [];
    const bounceTrend = source.googleAnalytics?.bounceRateTrend ?? [];

    if (visitTrend.length >= 2) {
      const first = visitTrend[0].value;
      const last = visitTrend[visitTrend.length - 1].value;
      const direction = last >= first ? 'upward' : 'downward';
      highlights.push(`Visits trend is ${direction} across the latest tracked periods.`);
    }

    if (bounceTrend.length >= 2) {
      const first = bounceTrend[0].value;
      const last = bounceTrend[bounceTrend.length - 1].value;
      const direction = last <= first ? 'improving' : 'softening';
      highlights.push(`Bounce-rate trend looks ${direction} over the recent period.`);
    }

    if (highlights.length === 0) {
      highlights.push('Trend detail is limited, so this section should lean on current-state KPIs.');
    }

    return highlights;
  }

  private buildTechnicalHighlights(source: SeoPdfSourceData): string[] {
    const issues = source.technicalHealth?.issues ?? [];
    const activeIssues = issues.filter((issue: SeoPdfTechnicalIssue) => issue.count > 0);

    if (activeIssues.length === 0) {
      return ['The current audit does not surface a dominant technical blocker.'];
    }

    return activeIssues
      .slice(0, 2)
      .map((issue: SeoPdfTechnicalIssue) => `${issue.theme} is one of the clearest grouped risks in the current scan.`);
  }

  private prioritizeTechnicalIssues(issues: SeoPdfTechnicalIssue[]): SeoPdfTechnicalIssue[] {
    return [...issues].sort((left: SeoPdfTechnicalIssue, right: SeoPdfTechnicalIssue) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }

      return left.theme.localeCompare(right.theme);
    });
  }

  private describeTechnicalPosture(issues: SeoPdfTechnicalIssue[]): string {
    const activeIssues = issues.filter((issue: SeoPdfTechnicalIssue) => issue.count > 0);
    if (activeIssues.length === 0) {
      return 'stable, with no major grouped issue theme currently surfaced';
    }

    return `constrained most by ${activeIssues[0].theme.toLowerCase()}`;
  }

  private getMomentumLabel(source: SeoPdfSourceData): string {
    if ((source.dashboard?.organicTraffic ?? 0) > 0 && (source.dashboard?.backlinks ?? 0) > 0) {
      return 'Improving';
    }

    if (source.dashboard?.organicTraffic || source.dashboard?.backlinks) {
      return 'Flat';
    }

    return 'Unknown';
  }

  private buildMetricCard(
    label: string,
    value: number | string | null | undefined,
    isPercent: boolean = false,
    unit?: string
  ): SeoPdfMetricCard {
    return {
      label,
      value: this.formatMetricValue(value, isPercent, unit),
      tone: value === null || value === undefined ? 'neutral' : 'info',
    };
  }

  private formatMetricValue(
    value: number | string | null | undefined,
    isPercent: boolean = false,
    unit?: string
  ): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (isPercent) {
      return `${value}%`;
    }

    if (unit === 'seconds') {
      return `${value}s`;
    }

    return new Intl.NumberFormat('en-US').format(value);
  }

  private buildOpportunityClusterKey(item: OpportunityItem): string {
    const normalizedTitle = (item.title ?? '')
      .toLowerCase()
      .replace(/\d+/g, '')
      .replace(/[^a-z\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return `${item.type}:${normalizedTitle || item.type}`;
  }

  private buildOpportunityClusterLabel(item: OpportunityItem): string {
    if (item.type === 'CANNIBALIZATION') {
      return 'Resolve keyword cannibalization cluster';
    }

    if (item.type === 'TECHNICAL_FIX') {
      return 'Address grouped technical SEO fixes';
    }

    return item.title;
  }

  private buildRoadmapTitle(label: string): string {
    const normalizedLabel = label.replace(/\s+/g, ' ').trim();
    if (normalizedLabel.length <= 56) {
      return normalizedLabel;
    }

    return `${normalizedLabel.slice(0, 53).trimEnd()}...`;
  }

  private buildRoadmapEvidence(cluster: SeoPdfOpportunityCluster): string {
    const primaryEvidence = cluster.representativeEvidence[0] ?? '';
    const cleanedEvidence = primaryEvidence
      .replace(/\s+/g, ' ')
      .replace(/^["']+|["']+$/g, '')
      .trim();

    if (!cleanedEvidence) {
      return `${cluster.count} grouped item(s) support this action.`;
    }

    if (cleanedEvidence.length <= 88) {
      return cleanedEvidence;
    }

    return `${cleanedEvidence.slice(0, 85).trimEnd()}...`;
  }

  private getOpportunityFamily(type: OpportunityType): string {
    switch (type) {
      case 'CTR_LIFT':
      case 'NEAR_PAGE_ONE':
      case 'DECAY':
      case 'BUSINESS_IMPACT_DECAY':
        return 'Ranking opportunity';
      case 'CONTENT_GAP':
      case 'COMPETITOR_CONTENT_GAP':
        return 'Content opportunity';
      case 'CANNIBALIZATION':
        return 'Content consolidation';
      case 'BACKLINK_GAP':
      case 'LINK_INTERSECT':
        return 'Backlink opportunity';
      case 'TECHNICAL_FIX':
      case 'TRAFFIC_QUALITY_LEAK':
        return 'Technical fix';
      case 'LOCAL':
      case 'REVIEW_REPUTATION_RISK':
      case 'REVIEW_RATING_DECLINE':
        return 'Local and reviews';
      case 'COMPETITOR_RANK_LOSS':
        return 'Competitive response';
      case 'CAMPAIGN_HALO':
        return 'Channel overlap';
      default:
        return 'SEO opportunity';
    }
  }

  private extractEvidenceSummaries(item: OpportunityItem): string[] {
    return (item.evidence ?? [])
      .map((evidence) => evidence.summary?.trim())
      .filter((summary: string | undefined): summary is string => !!summary);
  }

  private mergeUniqueStrings(existing: string[], incoming: string[]): string[] {
    return [...new Set([...existing, ...incoming])];
  }

  private mergeOpportunityTypes(existing: OpportunityType[], next: OpportunityType): OpportunityType[] {
    return existing.includes(next) ? existing : [...existing, next];
  }

  private mergeEffort(
    current: OpportunityEffort | 'UNKNOWN',
    next: OpportunityEffort | undefined
  ): OpportunityEffort | 'UNKNOWN' {
    if (!next) {
      return current;
    }

    const weights: Record<OpportunityEffort | 'UNKNOWN', number> = {
      UNKNOWN: 0,
      LOW: 1,
      MEDIUM: 2,
      HIGH: 3,
    };

    return weights[next] > weights[current] ? next : current;
  }
}
